/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Claims from '../types/claims';
import { OidcService } from '../types/oidc-service';
import { useOidc } from './oidc/oidc';

export interface OidcContextProps {
  oidcService: OidcService;
  isAuthenticated: () => boolean;
  checkSession: () => Promise<boolean>;
  getAccessToken: () => Promise<string | null>;
  getClaims: () => Promise<Claims>;
  signinRedirect: () => void;
  forbiddenRedirect: () => void;
  logoutRedirect: () => Promise<void>;
  signinRedirectCallback: () => void;
  onLogout: (handler: () => void) => void;
}

export const AdminInterceptContext = createContext<OidcContextProps>(null!);

export function AdminInterceptProvider(props: React.PropsWithChildren<any>) {
  const oidcClient = useOidc();
  const { search } = useLocation();
  const [masterAccountId, setMasterAccountId] = useState<string | null>();
  const [locationId, setLocationId] = useState<string | null>();
  const [loaded, setLoaded] = useState<boolean>(false);

  const query = useMemo(() => new URLSearchParams(search), [search]);

  useEffect(() => {
    const accountId = query.get('customerId');
    const locationId = query.get('locationId');
    if (accountId) setMasterAccountId(accountId);
    if (locationId) setLocationId(locationId);
    setLoaded(true);
  }, [query]);

  const getClaims = useMemo(() => {
    return async (): Promise<Claims> => {
      let claims = await oidcClient.getClaims();
      if (masterAccountId && !claims.masterAccountId) {
        claims = { ...claims, masterAccountId: parseInt(masterAccountId) };
      }
      if(locationId && !claims.locationId){
        claims = { ...claims, locationId: parseInt(locationId) };
      }
      return claims;
    };
  }, [oidcClient.getClaims, masterAccountId]);

  const oidcService: OidcService = useMemo(() => {
    const service = oidcClient.oidcService;
    return { ...service, getClaims: getClaims };
  }, [getClaims, oidcClient]);

  if (loaded) {
    return (
      <AdminInterceptContext.Provider value={{...oidcClient,  getClaims: getClaims, oidcService: oidcService }}>
        {props.children}
      </AdminInterceptContext.Provider>
    );
  } else {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }
}

export function useOidcWithAdmin() {
  const context = useContext(AdminInterceptContext);
  if (!context) {
    throw new Error('useOidcWithAdmin must be used within the AdminInterceptProvider');
  }
  return context;
}
